import { i as isMacOS, h as hasKeyboard } from './device-utils-c540e212.js';
import { h, F as Fragment } from './index-6e9159ae.js';
import { B as Button } from './button-ab6bd1ba.js';

class SearchBoxCommon {
  constructor(props) {
    this.props = props;
  }
  get popupId() {
    return `${this.props.id}-popup`;
  }
  get hasSuggestions() {
    return !!this.props.getAllSuggestionElements().length;
  }
  get hasActiveDescendant() {
    return this.props.getActiveDescendant() !== '';
  }
  get firstValue() {
    var _a;
    return (_a = this.props.getPanelInFocus()) === null || _a === void 0 ? void 0 : _a.firstElementChild;
  }
  get lastValue() {
    var _a;
    return (_a = this.props.getPanelInFocus()) === null || _a === void 0 ? void 0 : _a.lastElementChild;
  }
  get nextOrFirstValue() {
    var _a;
    if (!this.hasActiveDescendant) {
      return this.firstValue;
    }
    return (((_a = this.props.getActiveDescendantElement()) === null || _a === void 0 ? void 0 : _a.nextElementSibling) ||
      this.firstValue);
  }
  get previousOrLastValue() {
    var _a;
    if (!this.hasActiveDescendant) {
      return this.lastValue;
    }
    return (((_a = this.props.getActiveDescendantElement()) === null || _a === void 0 ? void 0 : _a.previousElementSibling) ||
      this.lastValue);
  }
  get showSuggestions() {
    return (this.hasSuggestions &&
      this.props.getIsExpanded() &&
      !this.props.getIsSearchDisabled());
  }
  scrollActiveDescendantIntoView() {
    var _a;
    (_a = this.props.getActiveDescendantElement()) === null || _a === void 0 ? void 0 : _a.scrollIntoView({
      block: 'nearest',
    });
  }
  focusNextValue() {
    if (!this.hasSuggestions || !this.nextOrFirstValue) {
      return;
    }
    this.props.focusValue(this.nextOrFirstValue);
  }
  focusPreviousValue() {
    if (!this.hasSuggestions || !this.previousOrLastValue) {
      return;
    }
    this.props.focusValue(this.previousOrLastValue);
  }
  updateQuery(query) {
    this.props.bindings.engine.dispatch(this.props.querySetActions.updateQuerySetQuery({
      id: this.props.id,
      query,
    }));
  }
  onSuggestionClick(item, e) {
    item.onSelect && item.onSelect(e);
    item.query && this.props.clearSuggestions();
  }
  getSearchInputLabel(minimumQueryLength = 0) {
    if (this.props.getIsSearchDisabled()) {
      return this.props.bindings.i18n.t('search-disabled', {
        length: minimumQueryLength,
      });
    }
    if (isMacOS()) {
      return this.props.bindings.i18n.t('search-box-with-suggestions-macos');
    }
    if (!hasKeyboard()) {
      return this.props.bindings.i18n.t('search-box-with-suggestions-keyboardless');
    }
    return this.props.bindings.i18n.t('search-box-with-suggestions');
  }
}

const SearchBoxWrapper = (props, children) => {
  const getClasses = () => {
    const baseClasses = 'flex bg-background w-full border border-neutral rounded-md focus-within:ring';
    const focusClasses = props.disabled
      ? 'focus-within:border-disabled focus-within:ring-neutral'
      : 'focus-within:border-primary focus-within:ring-ring-primary';
    const inputTypeClasses = props.textArea
      ? 'absolute top-0 left-0'
      : 'relative h-full';
    return [baseClasses, focusClasses, inputTypeClasses].join(' ');
  };
  return (h("div", { part: "wrapper", class: getClasses() }, children));
};

const ClearIcon = `<svg viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg"><path d="m18 2-1.8-2-7.1 7.1-7.1-7.1-2 2 7.1 7.1-7.1 7.1 2 1.8 7.1-6.9 7.1 6.9 1.8-1.8-6.9-7.1z"/></svg>`;

const ClearButton = ({ inputRef, bindings, onClick, ...defaultButtonProps }) => (h(Button, { style: "text-transparent", part: "clear-button", class: "w-8 h-8 mr-1.5 text-neutral-dark shrink-0", onClick: () => {
    onClick === null || onClick === void 0 ? void 0 : onClick();
    inputRef === null || inputRef === void 0 ? void 0 : inputRef.focus();
  }, ariaLabel: bindings.i18n.t('clear'), ...defaultButtonProps },
  h("atomic-icon", { part: "clear-icon", icon: ClearIcon, class: "w-3 h-3" })));

function getPopupAttributes(props) {
  return {
    role: 'combobox',
    autocomplete: 'off',
    autocapitalize: 'off',
    autocorrect: 'off',
    'aria-activedescendant': props.activeDescendant,
    'aria-expanded': `${props.hasSuggestions && props.expanded}`,
    'aria-autocomplete': 'both',
    'aria-haspopup': 'true',
    'aria-controls': props.id,
  };
}
const SearchInput = ({ inputRef, loading, bindings, onKeyDown, value, ariaLabel, onClear, popup, ...defaultInputProps }) => (h("div", { class: "grow flex items-center" },
  h("input", { part: "input", "aria-label": ariaLabel, placeholder: bindings.i18n.t('search'), type: "text", class: "h-full outline-none bg-transparent w-0 grow px-4 py-3.5 text-neutral-dark placeholder-neutral-dark text-lg", onKeyDown: (e) => {
      onKeyDown === null || onKeyDown === void 0 ? void 0 : onKeyDown(e);
    }, ...(popup && getPopupAttributes(popup)), ...defaultInputProps, value: value }),
  loading && (h("span", { part: "loading", class: "loading w-5 h-5 rounded-full bg-gradient-to-r animate-spin mr-3 grid place-items-center" })),
  !loading && value && (h(ClearButton, { inputRef: inputRef, bindings: bindings, onClick: () => onClear() }))));

const queryDataAttribute = 'data-query';
function getPartsForSuggestion(suggestion, isSelected) {
  let part = 'suggestion';
  if (isSelected) {
    part += ' active-suggestion';
  }
  if (suggestion.query) {
    part += ' suggestion-with-query';
  }
  if (suggestion.part) {
    part += ` ${suggestion.part}`;
  }
  return part;
}
function getClassesForSuggestion(isSelected) {
  return `flex px-4 min-h-[40px] items-center text-left text-neutral-dark hover:bg-neutral-light cursor-pointer ${isSelected ? 'bg-neutral-light' : ''}`;
}
function isHTMLElement(el) {
  return el instanceof HTMLElement;
}
function ensureContentForRenderedSuggestion({ renderedSuggestion, suggestion, }) {
  if (isHTMLElement(suggestion.content)) {
    renderedSuggestion.replaceChildren(suggestion.content);
  }
}
function getAriaLabelForRenderedSuggestion({ bindings, renderedSuggestion, suggestion, side, index, lastIndex, isDoubleList, isButton, }) {
  var _a, _b, _c;
  const contentLabel = (_c = (_b = (_a = suggestion.ariaLabel) !== null && _a !== void 0 ? _a : suggestion.query) !== null && _b !== void 0 ? _b : renderedSuggestion.innerText) !== null && _c !== void 0 ? _c : bindings.i18n.t('no-title');
  const labelWithType = isMacOS() && isButton
    ? bindings.i18n.t('search-suggestion-button', {
      label: contentLabel,
      interpolation: { escapeValue: false },
    })
    : contentLabel;
  const position = index + 1;
  const count = lastIndex + 1;
  if (!isDoubleList) {
    return bindings.i18n.t('search-suggestion-single-list', {
      label: labelWithType,
      position,
      count,
      interpolation: { escapeValue: false },
    });
  }
  return bindings.i18n.t('search-suggestion-double-list', {
    label: labelWithType,
    position,
    count,
    side: bindings.i18n.t(side === 'left' ? 'left' : 'right'),
    interpolation: { escapeValue: false },
  });
}
function getContentForSuggestion(suggestion) {
  return !isHTMLElement(suggestion.content) ? (suggestion.content) : (h(Fragment, null));
}
function getCommonSearchSuggestionAttributes({ bindings, id, suggestion, isSelected, side, index, lastIndex, isDoubleList, }, isButton) {
  return {
    id: id,
    key: suggestion.key,
    part: getPartsForSuggestion(suggestion, isSelected),
    class: getClassesForSuggestion(isSelected),
    ref: (el) => {
      if (!el) {
        return;
      }
      ensureContentForRenderedSuggestion({
        renderedSuggestion: el,
        suggestion,
      });
      el.setAttribute('aria-label', getAriaLabelForRenderedSuggestion({
        bindings,
        renderedSuggestion: el,
        suggestion,
        side,
        index,
        lastIndex,
        isDoubleList,
        isButton,
      }));
    },
  };
}
const SimpleSearchSuggestion = (props) => {
  return (h("span", { ...getCommonSearchSuggestionAttributes(props, false) }, getContentForSuggestion(props.suggestion)));
};
const ButtonSearchSuggestion = (props) => {
  return (h("button", { ...getCommonSearchSuggestionAttributes(props, true), onMouseDown: (e) => e.preventDefault(), onClick: (e) => { var _a; return (_a = props.onClick) === null || _a === void 0 ? void 0 : _a.call(props, e); }, onMouseOver: (e) => { var _a; return (_a = props.onMouseOver) === null || _a === void 0 ? void 0 : _a.call(props, e); }, [queryDataAttribute]: props.suggestion.query }, getContentForSuggestion(props.suggestion)));
};

export { ButtonSearchSuggestion as B, SearchBoxCommon as S, SearchInput as a, SearchBoxWrapper as b, SimpleSearchSuggestion as c, queryDataAttribute as q };

//# sourceMappingURL=search-suggestion-07a5514d.js.map